/**
 * The final report's figures Teslyn fills in from the project (MB, decision 898; docs/specs/project-reporting/
 * final-report.md §4.8). The starting text (`previousProgramWording`) leaves the values the previous reporting program
 * took from the job as blanks, `[fill in: …]`. When the final report is printed, Teslyn replaces the blanks it knows
 * with the project's own figures, the same values Teslyn's QC uses:
 *
 *   blank                                        from
 *   thorium limit of the calibrations            qcLimits(project).handSamplePct (Project settings, QC limits)
 *   thorium limit of the test lines              qcLimits(project).testLinePct
 *   non-linear limit, non-linear window          projectQcRules(project) 'diurnal_nonlinear' (limit, window)
 *   chord length, chord limit                    projectQcRules(project) 'diurnal_chord' (window, limit)
 *   base station magnetometer, G856 or G823      the project's base station files in Teslyn Field (basemag_series)
 *
 * A figure Teslyn cannot find (a rule turned off, no base station file imported, or a blank Teslyn does not know)
 * stays as the visible blank. The stored text keeps its blanks, so the same text serves every project.
 */
import { qcLimits } from './calibration.js';
import { projectQcRules, qcRulesSource } from './geosoft-qc.js';
import { formatNumber, qcRule, type QcRuleKey } from './qc-spec.js';
import type { BasemagSeries } from './basemag.js';
import type { Project } from './model.js';
import { textBlanks, type FinalReportText } from './final-report-text.js';

/** The figures Teslyn fills in. */
export type ProjectFigureKey =
  | 'thorium_cal'
  | 'thorium_test_line'
  | 'nonlinear_nt'
  | 'nonlinear_min'
  | 'chord_min'
  | 'chord_nt'
  | 'base_station';

export interface ProjectFigureDef {
  readonly key: ProjectFigureKey;
  /** The blank as the starting text writes it. */
  readonly blank: string;
  /** What the figure is, in words. */
  readonly label: string;
  /** Where each project's figure comes from, in words (Teslyn Cloud's company text, where no project is open). */
  readonly fromEachProject: string;
  /** The words after "fill in:" that name it (lower case, single spaces); the first is the starting text's. */
  readonly names: readonly string[];
}

const QC_LIMITS = 'Project settings, QC limits';

export const PROJECT_FIGURES: readonly ProjectFigureDef[] = [
  {
    key: 'thorium_cal',
    blank: '[fill in: thorium limit of the calibrations]',
    label: 'Thorium limit of the hand sample calibrations',
    fromEachProject: `the project's hand sample thorium limit (${QC_LIMITS})`,
    names: ['thorium limit of the calibrations'],
  },
  {
    key: 'thorium_test_line',
    blank: '[fill in: thorium limit of the test lines]',
    label: 'Thorium limit of the test lines',
    fromEachProject: `the project's test line thorium limit (${QC_LIMITS})`,
    names: ['thorium limit of the test lines'],
  },
  {
    key: 'nonlinear_nt',
    blank: '[fill in: non-linear limit]',
    label: 'Non-linear diurnal limit, nT',
    fromEachProject: "the project's non-linear diurnal reflight rule (its QC limits)",
    names: ['non-linear limit'],
  },
  {
    key: 'nonlinear_min',
    blank: '[fill in: non-linear window]',
    label: 'Non-linear diurnal window, minutes',
    fromEachProject: "the project's non-linear diurnal reflight rule (its QC limits)",
    names: ['non-linear window'],
  },
  {
    key: 'chord_min',
    blank: '[fill in: chord length]',
    label: 'Diurnal chord length, minutes',
    fromEachProject: "the project's diurnal chord reflight rule (its QC limits)",
    names: ['chord length'],
  },
  {
    key: 'chord_nt',
    blank: '[fill in: chord limit]',
    label: 'Diurnal chord limit, nT',
    fromEachProject: "the project's diurnal chord reflight rule (its QC limits)",
    names: ['chord limit'],
  },
  {
    key: 'base_station',
    blank: '[fill in: base station magnetometer, G856 or G823]',
    label: 'Base station magnetometer model',
    fromEachProject: 'the base station files imported for the project in Teslyn Field (Diurnal QC)',
    names: ['base station magnetometer, g856 or g823', 'base station magnetometer'],
  },
];

const DEF: Readonly<Record<ProjectFigureKey, ProjectFigureDef>> = Object.fromEntries(
  PROJECT_FIGURES.map((d) => [d.key, d]),
) as Record<ProjectFigureKey, ProjectFigureDef>;

const normalise = (s: string): string => s.toLowerCase().replace(/\s+/g, ' ').trim();

/** The figure a blank names (`[fill in: chord limit]` → chord_nt), whatever its case and spacing; null if none. */
export function projectFigureForBlank(blank: string): ProjectFigureKey | null {
  const m = /^\[\s*fill in:([^\]]*)\]$/i.exec(blank.trim());
  if (!m) return null;
  const name = normalise(m[1]!);
  return PROJECT_FIGURES.find((d) => d.names.includes(name))?.key ?? null;
}

// ---------------------------------------------------------------------------------------- the project's figures

/** How a figure fares when the report is printed. */
export type FigureFill =
  /** Teslyn has it: the blank prints as the value. */
  | 'filled'
  /** Teslyn cannot find it: the blank prints as it is. */
  | 'blank'
  /** Known only on the laptop that prints the report (the base station files): shown in Teslyn Cloud. */
  | 'on_laptop';

export interface ProjectFigure {
  readonly key: ProjectFigureKey;
  readonly fill: FigureFill;
  /** What replaces the blank; null unless filled. */
  readonly value: string | null;
  /** Where the value comes from, or why it stays blank, in words. */
  readonly from: string;
}

export type ProjectFigures = Readonly<Record<ProjectFigureKey, ProjectFigure>>;

/** A base station file of the project as Teslyn Field keeps it (`basemag_series`). */
export type BaseStationRecord = Pick<
  BasemagSeries,
  'source_format' | 'instrument' | 'station_id' | 'deleted_at'
>;

export interface ProjectFigureSources {
  readonly project: Pick<Project, 'qc_hand_sample_pct' | 'qc_test_line_pct' | 'qc_rules'>;
  /**
   * The project's base station files (Teslyn Field's `basemag_series`), or null where they are not kept (Teslyn
   * Cloud: the laptop that prints the report fills the model).
   */
  readonly baseStations: readonly BaseStationRecord[] | null;
}

const set = (v: number | null | undefined): boolean => typeof v === 'number' && Number.isFinite(v) && v > 0;

/**
 * Thomson's export station ids of a `.raw` written from another instrument (basemag.ts: MagStation 60, 823 82, 862
 * 86): F26048's `F26048_D291_Mag_Station.raw` is the MagStation's day, station 60.
 */
const RAW_EXPORT_STATION: Readonly<Record<string, string>> = {
  '60': 'Geometrics MagStation',
  '82': 'Geometrics G-823',
  '86': 'Geometrics G-862',
};

/**
 * The magnetometer model of one base station file, as the report prints it; null for data that is not the project's
 * own base station magnetometer (an observatory) or names no model (a TA Live unit).
 */
export function baseStationModelName(r: BaseStationRecord): string | null {
  switch (r.source_format) {
    case 'magstation_dbt':
    case 'magstation_csv':
      return 'Geometrics MagStation';
    case 'g823_dat':
      return 'Geometrics G-823';
    case 'g862_txt':
      return 'Geometrics G-862';
    case 'g857_raw':
      return RAW_EXPORT_STATION[r.station_id.trim()] ?? 'Geometrics G-857';
    default:
      return null;
  }
}

/**
 * The base station magnetometer model of the project: every model its files name, the most files first ("Geometrics
 * MagStation", or "Geometrics MagStation and Geometrics G-823" with two in tandem).
 */
export function baseStationFigure(records: readonly BaseStationRecord[] | null): ProjectFigure {
  const key = 'base_station';
  if (records === null)
    return {
      key,
      fill: 'on_laptop',
      value: null,
      from:
        'Filled by Teslyn Field when it prints the report, from the base station files imported for the project ' +
        '(Diurnal QC); it stays blank if there are none.',
    };
  const files = records.filter((r) => r.deleted_at === null);
  const count = new Map<string, number>();
  for (const r of files) {
    const name = baseStationModelName(r);
    if (name) count.set(name, (count.get(name) ?? 0) + 1);
  }
  const names = [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([n]) => n);
  if (names.length === 0)
    return {
      key,
      fill: 'blank',
      value: null,
      from:
        files.length === 0
          ? 'No base station file is imported for the project (Diurnal QC), so Teslyn does not know the model.'
          : "The project's base station data (TA Live or an observatory) names no magnetometer model.",
    };
  const n = [...count.values()].reduce((a, b) => a + b, 0);
  return {
    key,
    fill: 'filled',
    value: names.join(' and '),
    from: `The project's ${n} base station file${n === 1 ? '' : 's'} imported in Teslyn Field (Diurnal QC).`,
  };
}

/** The project's figures, as the final report fills them (and Teslyn Cloud and Teslyn Field show them). */
export function projectFigures(src: ProjectFigureSources): ProjectFigures {
  const { project } = src;
  const limits = qcLimits(project);
  const thorium = (key: ProjectFigureKey, own: number | null | undefined, v: number): ProjectFigure => ({
    key,
    fill: 'filled',
    value: `±${formatNumber(v)} %`,
    from: set(own)
      ? `${QC_LIMITS}.`
      : "Teslyn's default, which the calibration checks use while Project settings has no limit of its own.",
  });
  const rules = projectQcRules(project);
  const rulesFrom =
    qcRulesSource(project) === 'project'
      ? "The project's QC limits (from its contract)."
      : "The company's default QC limits (the project has none of its own).";
  const rule = (
    key: ProjectFigureKey,
    rk: QcRuleKey,
    pick: 'limit' | 'window_min',
    ruleName: string,
  ): ProjectFigure => {
    const r = qcRule(rules, rk);
    if (!r || !r.enabled)
      return {
        key,
        fill: 'blank',
        value: null,
        from: `The project's QC limits have the ${ruleName} rule turned off.`,
      };
    const v = pick === 'limit' ? r.limit : r.window_min;
    if (!set(v))
      return {
        key,
        fill: 'blank',
        value: null,
        from: `The project's ${ruleName} rule has no ${pick === 'limit' ? 'limit' : 'time'}.`,
      };
    return { key, fill: 'filled', value: formatNumber(v!), from: rulesFrom };
  };
  return {
    thorium_cal: thorium('thorium_cal', project.qc_hand_sample_pct, limits.handSamplePct),
    thorium_test_line: thorium('thorium_test_line', project.qc_test_line_pct, limits.testLinePct),
    nonlinear_nt: rule('nonlinear_nt', 'diurnal_nonlinear', 'limit', 'non-linear diurnal'),
    nonlinear_min: rule('nonlinear_min', 'diurnal_nonlinear', 'window_min', 'non-linear diurnal'),
    chord_min: rule('chord_min', 'diurnal_chord', 'window_min', 'diurnal chord'),
    chord_nt: rule('chord_nt', 'diurnal_chord', 'limit', 'diurnal chord'),
    base_station: baseStationFigure(src.baseStations),
  };
}

// ---------------------------------------------------------------------------------------- filling the text

/** A blank, as `textBlanks` finds it. */
const BLANK = /\[fill in:[^\]]*\]/g;

/**
 * The text as the final report prints it: each blank Teslyn has a figure for replaced by it, every other blank left
 * as it is. The stored text is never changed (it keeps its blanks for the next project).
 */
export function fillProjectFigures(text: FinalReportText, figures: ProjectFigures): FinalReportText {
  const fill = (s: string): string =>
    s.replace(BLANK, (b) => {
      const key = projectFigureForBlank(b);
      const f = key ? figures[key] : null;
      return f && f.fill === 'filled' && f.value !== null ? f.value : b;
    });
  return {
    sections: text.sections.map((s) => ({ ...s, heading: fill(s.heading), text: fill(s.text) })),
    equipment: text.equipment.map((e) => ({
      ...e,
      instrument: fill(e.instrument),
      model: fill(e.model),
      serial: fill(e.serial),
    })),
  };
}

/** One blank of the text, and what the printed report does with it. */
export interface BlankPlanItem {
  /** Where it is ("Base station magnetometer", "Base station magnetometer, model"). */
  readonly where: string;
  readonly blank: string;
  /** The figure it names; null for a blank Teslyn does not fill. */
  readonly key: ProjectFigureKey | null;
  /**
   * filled: prints as `value`; blank: prints as it is; on_laptop: Teslyn Field fills it when it prints;
   * from_each_project: the company's text, filled from each project when printed.
   */
  readonly fill: FigureFill | 'from_each_project';
  readonly value: string | null;
  readonly from: string;
}

/**
 * Every blank of the text with what the printed report does with it: with a project's figures (Teslyn Field, a
 * project's tab in Teslyn Cloud), or for the company's text (no project: `figures` null).
 */
export function blankPlan(text: FinalReportText, figures: ProjectFigures | null): BlankPlanItem[] {
  return textBlanks(text).map(({ where, blank }) => {
    const key = projectFigureForBlank(blank);
    if (key === null)
      return {
        where,
        blank,
        key,
        fill: 'blank',
        value: null,
        from: 'Teslyn does not fill this blank: replace it in the text.',
      };
    if (figures === null)
      return {
        where,
        blank,
        key,
        fill: 'from_each_project',
        value: null,
        from: `Filled when printed from ${DEF[key].fromEachProject}.`,
      };
    const f = figures[key];
    return { where, blank, key, fill: f.fill, value: f.value, from: f.from };
  });
}

/** The plan in counts: how many blanks are filled, stay blank, or are filled on the laptop / per project. */
export function blankPlanCounts(plan: readonly BlankPlanItem[]): {
  readonly filled: number;
  readonly blank: number;
  readonly later: number;
} {
  return {
    filled: plan.filter((p) => p.fill === 'filled').length,
    blank: plan.filter((p) => p.fill === 'blank').length,
    later: plan.filter((p) => p.fill === 'on_laptop' || p.fill === 'from_each_project').length,
  };
}
